import { useState } from "react";
import { formatDate } from "../lib/formatDate";
import type { AiUsageMeter } from "../types";

// Top-of-app banner that surfaces trial countdown / subscription state
// and routes the user to the in-app Plan & Billing section when they
// want to upgrade. Hidden entirely once the user is on an active
// plan with no scheduled cancellation.
//
// Props come from the /api/me + /api/ai/usage-meter responses
// (piggy-backed onto the existing profile fetch). This component
// never triggers its own round-trip.

interface Props {
  plan: string;
  daysRemaining: number | null;
  // Active users who've clicked "Cancel" in the Stripe portal keep
  // plan === 'active' until their paid period ends. We surface this
  // as its own amber banner (severity "ending") so the user knows
  // when access stops and can resubscribe in one click.
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  // AI usage snapshot. Drives the "N/100 AI calls used" fragment in
  // the banner body. Optional because the /api/ai/usage-meter
  // request is independent of /api/me and may land later; we render
  // the banner without the fragment if it's null.
  aiMeter: AiUsageMeter | null;
  // Upgrade CTA. Spec routes Upgrade to the Plan & Billing section
  // in Settings rather than straight to Stripe Checkout — the
  // Settings page is the single jumping-off point for all billing
  // actions (portal + cadence pick).
  onOpenPlanAndBilling: () => void;
}

// Severity tier determines both the CSS class used and the urgency
// copy. Kept internal so AuthenticatedApp can just pass the raw plan
// fields and let this component decide what to render.
type Severity =
  | "info"
  | "warn"
  | "urgent"
  | "cancelled"
  | "ending"
  | "none";

function classify(
  plan: string,
  daysRemaining: number | null,
  cancelAtPeriodEnd: boolean,
): Severity {
  if (plan === "active") {
    // Active + cancelling — show the ending-soon amber banner. Active
    // + not cancelling — no banner at all.
    return cancelAtPeriodEnd ? "ending" : "none";
  }
  if (plan === "cancelled") return "cancelled";
  if (plan === "trial") {
    if (daysRemaining === null) return "info";
    if (daysRemaining <= 0) return "urgent";
    if (daysRemaining <= 7) return "warn";
    return "info";
  }
  // expired / unknown — treat as cancelled so the user still sees a
  // way back in.
  return "cancelled";
}

function formatEndDate(iso: string | null): string {
  if (!iso) return "the end of your billing period";
  return formatDate(iso, "long") || "the end of your billing period";
}

// Session-scoped dismiss key. Namespaced by plan + severity so the
// banner comes back when the user moves from e.g. "info" (plenty of
// trial left) to "warn" (<7 days) — a state change the user should
// see even if they dismissed the milder variant earlier in the
// session.
function dismissKey(severity: Severity): string {
  return `casethread:trial-banner-dismissed:${severity}`;
}

function isDismissed(severity: Severity): boolean {
  try {
    return (
      window.sessionStorage.getItem(dismissKey(severity)) === "1"
    );
  } catch {
    return false;
  }
}

export default function TrialBanner({
  plan,
  daysRemaining,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  aiMeter,
  onOpenPlanAndBilling,
}: Props) {
  const severity = classify(plan, daysRemaining, cancelAtPeriodEnd);
  const [dismissed, setDismissed] = useState<boolean>(() =>
    isDismissed(severity),
  );

  if (severity === "none") return null;
  // "urgent" (trial expired) is never dismissible — the user cannot
  // use AI features until they upgrade, and hiding that reality
  // would just surface later as a confusing error inside Interrogate.
  const dismissible = severity !== "urgent";
  if (dismissed && dismissible) return null;

  function dismiss() {
    try {
      window.sessionStorage.setItem(dismissKey(severity), "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }

  // AI-usage fragment: "· 52/100 AI calls used" — only rendered for
  // trial / warn / urgent states. Hidden for "ending" (active-but-
  // cancelling) because active users are on the 500-call plan and
  // showing "52/500" alongside "ends on DATE" is noisy.
  const usageFragment = (() => {
    if (!aiMeter || aiMeter.byo_key_active) return null;
    if (severity !== "info" && severity !== "warn" && severity !== "urgent") {
      return null;
    }
    if (aiMeter.limit === null) return null;
    return (
      <>
        {" · "}
        <strong>{aiMeter.ai_calls.toLocaleString()}</strong>
        {"/"}
        <strong>{aiMeter.limit.toLocaleString()}</strong>
        {" AI calls used"}
      </>
    );
  })();

  // Message varies by severity.
  let message: React.ReactNode;
  if (severity === "ending") {
    message = (
      <>
        Your subscription ends on{" "}
        <strong>{formatEndDate(currentPeriodEnd)}</strong>. You'll keep
        access until then.
      </>
    );
  } else if (severity === "cancelled") {
    message = (
      <>Your subscription has ended.{usageFragment}</>
    );
  } else if (severity === "urgent") {
    message = (
      <>
        Trial expired · Upgrade to continue using AI features
        {usageFragment}
      </>
    );
  } else if (severity === "warn") {
    message = (
      <>
        Free trial · <strong>{daysRemaining}</strong>{" "}
        {daysRemaining === 1 ? "day" : "days"} remaining
        {usageFragment}
      </>
    );
  } else {
    // info
    message = (
      <>
        Free trial · <strong>{daysRemaining}</strong>{" "}
        {daysRemaining === 1 ? "day" : "days"} remaining
        {usageFragment}
      </>
    );
  }

  return (
    <div
      className={`trial-banner trial-banner-${severity}`}
      role="status"
    >
      <span className="trial-banner-message">{message}</span>
      <div className="trial-banner-ctas">
        <button
          type="button"
          className="trial-banner-cta"
          onClick={onOpenPlanAndBilling}
        >
          {severity === "ending" ? "Manage subscription" : "Upgrade"}
        </button>
        {dismissible && (
          <button
            type="button"
            className="trial-banner-dismiss"
            onClick={dismiss}
            aria-label="Dismiss for this session"
            title="Dismiss for this session"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
